/* eslint-disable no-param-reassign */
/**
 * This component is ported from `react-audio-waveform` library
 * due to react 19 incompatibility issues
 *
 * {@link https://github.com/SoundstripeEngineering/react-audio-waveform}
 */
import { useEffect, useRef, useCallback } from 'react';

interface WaveCanvasProps {
  barWidth: number;
  width: number;
  height: number;
  peaks?: number[];
  pixelRatio?: number;
  color?: string;
  gradientColors?: (number | string)[][];
}

const defaultPixelRatio =
  typeof window !== 'undefined' ? window.devicePixelRatio : 1;

const absMax = (values: number[]) =>
  values.reduce((max, val) => {
    const num = Math.abs(val);
    return num > max ? num : max;
  }, -Infinity);

const WaveCanvas = ({
  barWidth,
  width,
  height,
  peaks,
  pixelRatio = defaultPixelRatio,
  color = 'ccc',
  gradientColors,
}: WaveCanvasProps) => {
  const waveRef = useRef<HTMLCanvasElement>(null);

  const clearWave = useCallback(
    (ctx: CanvasRenderingContext2D, w: number, h: number) => {
      ctx.clearRect(0, 0, w, h);
    },
    []
  );

  const drawBars = useCallback(
    (ctx: CanvasRenderingContext2D, w: number, p: number[]) => {
      if (!p) return;

      const params = {
        fillParent: true,
        height,
        normalize: true,
        pixelRatio,
        barWidth,
        color,
        gradientColors,
      };

      const hasMinVals = p.some((val) => val < 0);
      let processedPeaks = [...p];
      if (hasMinVals) {
        let indexOffset = 0;
        if (p[0] < 0) {
          indexOffset = 1;
        }
        processedPeaks = p.filter(
          (_, index) => (index + indexOffset) % 2 === 0
        );
      }

      const $ = 0.5 / params.pixelRatio;
      const offsetY = 0;
      const halfH = params.height / 2;
      const { length } = processedPeaks;
      const bar = params.barWidth ? params.barWidth * params.pixelRatio : 0;
      const gap = Math.max(params.pixelRatio, 2);
      const step = bar + gap;

      let absmax = 1;
      if (params.normalize && processedPeaks.length > 0) {
        absmax = absMax(processedPeaks);
      }

      const scale = w / length;

      if (params.gradientColors && ctx) {
        const gradient = ctx.createLinearGradient(0, 0, w, 0);
        params.gradientColors.forEach((clr) => {
          gradient.addColorStop(clr[0] as number, clr[1] as string);
        });
        ctx.fillStyle = gradient;
      } else if (ctx) {
        ctx.fillStyle = params.color || 'ccc';
      }

      if (!ctx) {
        return;
      }

      for (let i = 0; i < w; i += step) {
        const peakIndex = Math.floor(i / scale);
        if (processedPeaks[peakIndex] !== undefined) {
          let h = Math.round((processedPeaks[peakIndex] / absmax) * halfH);
          if (h === 0) {
            h = 1;
          }
          ctx.fillRect(i + $, halfH - h + offsetY, bar + $, h * 2);
        }
      }
    },
    [barWidth, color, gradientColors, height, pixelRatio]
  );

  const updateSize = useCallback(
    (w: number, h: number, p?: number[]) => {
      if (waveRef.current && p) {
        const waveCanvasCtx = waveRef.current.getContext('2d');
        if (!waveCanvasCtx) return;

        const displayWidth = Math.round(w / pixelRatio);
        const displayHeight = Math.round(h / pixelRatio);
        waveCanvasCtx.canvas.width = w;
        waveCanvasCtx.canvas.height = h;
        waveCanvasCtx.canvas.style.width = `${displayWidth}px`;
        waveCanvasCtx.canvas.style.height = `${displayHeight}px`;

        clearWave(waveCanvasCtx, w, h);
        drawBars(waveCanvasCtx, w, p);
      }
    },
    [clearWave, drawBars, pixelRatio]
  );

  useEffect(() => {
    updateSize(width, height, peaks);
  }, [updateSize, width, height, peaks]);

  return peaks ? <canvas className="progress" ref={waveRef} /> : null;
};

export default WaveCanvas;
